import * as qaApi from '@/services/recruiterQaApi'
import { getOwner } from '@/custom/classes/my_utils.js'
import {
  ticketTitle, isToastableNotification, isCancelledBySupervisor, isPrivateRow, isReAnswer,
  isGenuineQuestion, sanitizeRecruiterRow, groupTurns
} from '@/utils/recruiterQaFormat'

// Recruiter Q&A ("דבר עם המערכת") store.
// Polling state machine (CONTRACT §6 + SUPERVISOR_SPEC "Unread delivery"):
//   - polls GET /updates while the feature is enabled and the tab is visible,
//     EVEN with zero pending ids (management notes / approvals arrive on roots
//     with nothing pending)
//   - 3s with the drawer open, 15s closed; paused while document.hidden,
//     one immediate poll when the tab becomes visible again
//   - errors back off 10s, then 30s; 401 stops + clears; 404 hides + clears
//   - one request in flight; responses from an obsolete generation are ignored
//   - `ticket_updates` (every owned root): the first response is a baseline
//     (no historical toasts); later, a root whose notification_version grew is
//     refetched and toasted by last_notification.kind, deduped by
//     (root_id, notification_version)
//   - legacy server (no ticket_updates): a toast only on pending -> arrival
//   - `answer_refresh` (INSTRUCTION_SPEC (d)): a management re-answer job in
//     progress. Compared separately from notification versions: it drives the
//     "המערכת מכינה תשובה מעודכנת…" hint (the ONLY source of it: pending re-answer
//     rows are never returned to recruiters), but never toasts,
//     never touches unread and never reopens; when it settles the thread is
//     refetched silently (the published answer arrives as a normal notification)
// Privacy: management-only rows (supervisor_note / supervisor_instruction) and
// management attribution are dropped on the way in AND purged from cache on
// every thread load; the server already filters them.
// Tickets (CONTRACT 2): ticket = root question + children, id = root id.
// Lifecycle (closed_at) and unread (notification_version > read_version) are
// server-owned; the badge is the server's unread_counts.total.

export const PENDING_STATUSES = ['queued', 'running', 'consulting']
export const ARRIVAL_STATUSES = ['answered', 'needs_input', 'needs_human']
// A re-answer job is public only once it published one of these.
export const PUBLISHED_REANSWER_STATUSES = ['answered', 'needs_input']
export const POLL_OPEN_MS = 3000
export const POLL_CLOSED_MS = 15000
export const BACKOFF_FIRST_MS = 10000
export const BACKOFF_MAX_MS = 30000
export const MAX_IDS_PER_POLL = 20
export const TOAST_TEXT_MAX = 120

export const TABS = ['open', 'closed']

// Recruiter-facing processing labels (CONTRACT 2). Lifecycle is separate.
export const STATUS_LABELS = {
  queued: 'ממתינה למענה',
  running: 'בטיפול',
  consulting: 'בבדיקה נוספת',
  answered: 'נענתה',
  needs_input: 'נדרש מידע ממך',
  needs_human: 'ממתינה לטיפול אנושי',
  failed: 'המענה נכשל'
}

export const TICKET_ERROR_MESSAGES = {
  list: 'לא הצלחנו לטעון את הפניות כרגע. אפשר לנסות שוב בעוד רגע.',
  thread: 'לא הצלחנו לטעון את הפנייה כרגע. אפשר לנסות שוב בעוד רגע.',
  not_found: 'הפנייה לא נמצאה. ייתכן שהיא שייכת למשתמשת אחרת.',
  lifecycle: 'לא הצלחנו לעדכן את הפנייה כרגע. אפשר לנסות שוב בעוד רגע.'
}

// Shown (as a status line, not an error) when a ticket was transferred away.
export const TRANSFER_NOTICE = 'הפנייה הועברה ליועצת אחרת.'

export const pendingStorageKey = adminId => 'recruiter_qa_pending_' + adminId

export const isPendingStatus = status => PENDING_STATUSES.indexOf(status) !== -1
export const isArrivalStatus = status => ARRIVAL_STATUSES.indexOf(status) !== -1

export const toastKey = (rootId, version) => rootId + ':' + version

const activityMs = t => {
  const ms = t && t.last_activity_at ? Date.parse(t.last_activity_at) : NaN
  return isNaN(ms) ? 0 : ms
}

/** Sort root ids desc by (last_activity_at, root_id) — the server's list order. */
export const sortTicketIds = (ids, ticketsById) => ids.slice().sort((a, b) => {
  const diff = activityMs(ticketsById[b]) - activityMs(ticketsById[a])
  return diff !== 0 ? diff : b - a
})

export const tabOfTicket = t => (t && t.closed_at ? 'closed' : 'open')

export const isTicketUnread = t => !!t && (t.notification_version || 0) > (t.read_version || 0)

// The server may wrap the summary as {ticket: {...}}.
export const unwrapTicket = data => (data && data.ticket && typeof data.ticket === 'object') ? data.ticket : data

export const uuidv4 = () => {
  const c = (typeof window !== 'undefined' && window.crypto) ? window.crypto : null
  if (c && typeof c.randomUUID === 'function') {
    try { return c.randomUUID() } catch (e) { /* insecure context: fall through */ }
  }
  const bytes = new Uint8Array(16)
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes)
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.prototype.map.call(bytes, b => (b + 0x100).toString(16).substr(1)).join('')
  return hex.substr(0, 8) + '-' + hex.substr(8, 4) + '-' + hex.substr(12, 4) + '-' +
    hex.substr(16, 4) + '-' + hex.substr(20, 12)
}

export const toastText = text => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim()
  return clean.length > TOAST_TEXT_MAX ? clean.substr(0, TOAST_TEXT_MAX).trim() + '…' : clean
}

// The API may return the question object directly or wrapped as {question: {...}}.
// The object itself has a string `question` field, so the type tells them apart.
export const unwrapQuestion = data =>
  (data && data.question && typeof data.question === 'object') ? data.question : data

const defaultStorage = () => ({
  get (key) {
    try { return window.localStorage.getItem(key) } catch (e) { return null }
  },
  set (key, value) {
    try { window.localStorage.setItem(key, value) } catch (e) { /* ignore */ }
  },
  remove (key) {
    try { window.localStorage.removeItem(key) } catch (e) { /* ignore */ }
  }
})

const timeOf = q => {
  const t = q && q.updated_at ? Date.parse(q.updated_at) : NaN
  return isNaN(t) ? null : t
}

export const rootIdOf = (id, questions) => {
  let current = questions[id]
  if (!current) return id
  let hops = 0
  while (current && current.parent_id && hops < 10) {
    const parent = questions[current.parent_id]
    if (!parent) return current.parent_id
    current = parent
    hops++
  }
  return current ? current.id : id
}

// Summary fields that describe the ticket's lifecycle / latest activity.
const LIFECYCLE_FIELDS = ['last_activity_at', 'closed_at', 'latest_status']

const emptyCounts = () => ({ open: 0, closed: 0, total: 0 })

const initialState = () => ({
  enabled: false,
  // Server-decided (GET /config `supervisor`): gates the "פניות העובדות" menu only.
  supervisor: false,
  // Server-decided (GET /config `supervisor_writes`): supervisor action controls.
  supervisorWrites: false,
  // Server-decided (GET /config `jira_writes`): Jira decision buttons on the supervisor card.
  jiraWrites: false,
  adminId: null,
  adminDisplayName: '',
  // rows (questions) by id, and row id -> root id
  questions: {},
  rootByQid: {},
  pendingIds: [],
  // tickets
  ticketsById: {},
  openIds: [],
  closedIds: [],
  cursors: { open: null, closed: null },
  listMeta: { open: { loaded: false, loading: false, error: '' }, closed: { loaded: false, loading: false, error: '' } },
  threadsByRootId: {},
  unreadCounts: emptyCounts(),
  // drawer navigation
  drawerOpen: false,
  view: 'list',
  activeTab: 'open',
  selectedRootId: null,
  listScroll: { open: 0, closed: 0 },
  drafts: {},
  newContext: {},
  worker: { availability: null, last_seen_at: null },
  toastQueue: [],
  // One-line drawer notice after the open ticket was transferred away ('' = none)
  transferNotice: '',
  feedback: {},
  // root id -> { question_id, job_id, status, turn_revision } | null
  answerRefresh: {}
})

const listKey = tab => (tab === 'closed' ? 'closedIds' : 'openIds')

const dedupe = ids => {
  const seen = {}
  return ids.filter(id => {
    if (seen[id]) return false
    seen[id] = true
    return true
  })
}

export const createRecruiterQaModule = (overrides = {}) => {
  const deps = {
    api: qaApi,
    mapError: qaApi.mapError,
    storage: defaultStorage(),
    doc: typeof document !== 'undefined' ? document : null,
    hasFocus: () => (typeof document !== 'undefined' && typeof document.hasFocus === 'function') ? document.hasFocus() : true,
    now: () => Date.now(),
    // window.* so the browser (and jsdom fake timers in unit tests) own the clock.
    setTimeout: (fn, ms) => (typeof window !== 'undefined' ? window : global).setTimeout(fn, ms),
    clearTimeout: handle => (typeof window !== 'undefined' ? window : global).clearTimeout(handle),
    getAdminId: () => getOwner().id,
    uuid: uuidv4,
    ...overrides
  }

  // Non-reactive runtime of the poll loop (one per store instance).
  const rt = {
    timer: null,
    inFlight: false,
    generation: 0,
    errorCount: 0,
    initPromise: null,
    visibilityHandler: null,
    threadSeq: {},
    // Per-tab list sequence: bumped by every loadTickets AND by every local
    // change to the lists (submit / close / reopen, request and response), so
    // a list response that started before such a change is discarded.
    listSeq: { open: 0, closed: 0 },
    listReq: { open: 0, closed: 0 }, // seq of the newest loadTickets per tab
    // Monotonic stamp of local lifecycle changes; a summary fetched before a
    // ticket's stamp may not overwrite that ticket's lifecycle fields.
    lifecycleTick: 0,
    ackInFlight: {},
    toastKeys: {}, // (root_id:notification_version) already toasted this session
    // ticket_updates baseline: root_id -> last seen notification_version
    rootVersions: {},
    baselined: false
  }

  const invalidateLists = () => {
    TABS.forEach(tab => { rt.listSeq[tab]++ })
  }

  const isHidden = () => !!(deps.doc && deps.doc.hidden)

  const clearTimer = () => {
    if (rt.timer !== null) {
      deps.clearTimeout(rt.timer)
      rt.timer = null
    }
  }

  const persistPending = state => {
    if (state.adminId === null || state.adminId === undefined) return
    const key = pendingStorageKey(state.adminId)
    if (state.pendingIds.length) {
      deps.storage.set(key, JSON.stringify(state.pendingIds))
    } else {
      deps.storage.remove(key)
    }
  }

  const loadPending = adminId => {
    if (adminId === null || adminId === undefined) return []
    try {
      const parsed = JSON.parse(deps.storage.get(pendingStorageKey(adminId)) || '[]')
      return Array.isArray(parsed)
        ? parsed.map(n => parseInt(n, 10)).filter(n => n > 0).slice(0, 50)
        : []
    } catch (e) {
      return []
    }
  }

  const nextDelay = state => {
    if (rt.errorCount === 1) return BACKOFF_FIRST_MS
    if (rt.errorCount > 1) return BACKOFF_MAX_MS
    return state.drawerOpen ? POLL_OPEN_MS : POLL_CLOSED_MS
  }

  // Arms the next poll, or stops when there is nothing to wait for.
  const schedule = (ctx, delayOverride) => {
    clearTimer()
    const { state } = ctx
    if (!state.enabled) return
    if (isHidden()) return
    if (rt.inFlight) return
    const delay = (delayOverride === undefined || delayOverride === null) ? nextDelay(state) : delayOverride
    rt.timer = deps.setTimeout(() => {
      rt.timer = null
      ctx.dispatch('pollNow')
    }, delay)
  }

  // Stop everything and wipe in-memory state. Responses still in flight are
  // discarded by the generation bump.
  const stopAndClear = (ctx, { clearStorage = false } = {}) => {
    if (clearStorage) {
      ctx.commit('SET_PENDING', [])
      persistPending(ctx.state)
    }
    rt.generation++
    rt.inFlight = false
    rt.errorCount = 0
    rt.initPromise = null
    rt.ackInFlight = {}
    rt.toastKeys = {}
    rt.rootVersions = {}
    rt.baselined = false
    clearTimer()
    ctx.commit('RESET')
  }

  // Shared handling for errors that end the session for this feature.
  // Returns true when the error was fatal (401 / feature_off).
  const handleFatal = (ctx, err) => {
    if (err.kind === 'unauthorized') {
      stopAndClear(ctx)
      return true
    }
    if (err.kind === 'feature_off') {
      stopAndClear(ctx, { clearStorage: true })
      return true
    }
    return false
  }

  const rootOf = (state, id) => state.rootByQid[id] || rootIdOf(id, state.questions)

  // Ownership of a root is gone (transferred): forget it everywhere, without an
  // error. The notice is raised only when the recruiter had that thread open.
  const dropRoot = (ctx, rootId) => {
    const { state } = ctx
    if (!state.ticketsById[rootId] && !state.threadsByRootId[rootId] && state.selectedRootId !== rootId) return
    const wasOpen = state.selectedRootId === rootId && state.drawerOpen
    rt.threadSeq[rootId] = (rt.threadSeq[rootId] || 0) + 1 // discard in-flight loads
    delete rt.rootVersions[rootId]
    const stillPending = state.pendingIds.filter(id => rootOf(state, id) !== rootId)
    ctx.commit('DROP_ROOT', rootId)
    if (stillPending.length !== state.pendingIds.length) {
      ctx.commit('SET_PENDING', stillPending)
      persistPending(state)
    }
    if (wasOpen) ctx.commit('SET_TRANSFER_NOTICE', TRANSFER_NOTICE)
  }

  // Known roots (not changed locally since `since`) that the server no longer lists.
  const goneRoots = (state, presentIds, since) => {
    const present = {}
    presentIds.forEach(id => { present[id] = true })
    return Object.keys(state.ticketsById)
      .map(id => state.ticketsById[id])
      .filter(t => t && !present[t.root_id] && !((t._lifecycle_tick || 0) > (typeof since === 'number' ? since : 0)))
      .map(t => t.root_id)
  }

  const addPending = (ctx, ids) => {
    const fresh = ids.filter(id => ctx.state.pendingIds.indexOf(id) === -1)
    if (!fresh.length) return
    ctx.commit('SET_PENDING', ctx.state.pendingIds.concat(fresh))
    persistPending(ctx.state)
  }

  const applyCounts = (ctx, data) => {
    if (data && data.unread_counts && typeof data.unread_counts === 'object') ctx.commit('SET_UNREAD_COUNTS', data.unread_counts)
    if (data && data.worker) ctx.commit('SET_WORKER', data.worker)
  }

  // Merge a ticket summary and move it to the list its lifecycle says.
  // `since` = rt.lifecycleTick when the request producing it started;
  // `local` marks a lifecycle change made here (submit / close / reopen).
  const placeTicket = (ctx, ticket, { since, local = false } = {}) => {
    if (!ticket || !ticket.root_id) return
    const t = local ? { ...ticket, _lifecycle_tick: ++rt.lifecycleTick } : ticket
    ctx.commit('UPSERT_TICKETS', { tickets: [t], since })
    ctx.commit('PLACE_TICKET', ticket.root_id)
  }

  const newestQuestionId = (state, thread) => {
    const ids = (thread && thread.ids) || []
    for (let i = ids.length - 1; i >= 0; i--) {
      if (isGenuineQuestion(state.questions[ids[i]])) return ids[i]
    }
    return null
  }

  const enqueueToast = (ctx, toast) => {
    if (rt.toastKeys[toast.key]) return
    rt.toastKeys[toast.key] = true
    ctx.commit('ENQUEUE_TOAST', toast)
  }

  // answer_refresh of one root (ticket_updates entry or thread ticket). Absent
  // key = legacy server: ignored. The job row itself is never served to
  // recruiters, so it is not polled by id; ticket_updates carries its state. Returns true when
  // a refresh that was pending has settled or changed job (refetch the thread,
  // silently). Never toasts, never touches unread / versions / lifecycle.
  const applyAnswerRefresh = (ctx, root, holder) => {
    if (!holder || !Object.prototype.hasOwnProperty.call(holder, 'answer_refresh')) return false
    const raw = holder.answer_refresh
    const jobId = raw && parseInt(raw.job_id, 10)
    const next = (raw && typeof raw === 'object' && jobId > 0) ? {
      question_id: parseInt(raw.question_id, 10) || null,
      job_id: jobId,
      status: String(raw.status || ''),
      turn_revision: parseInt(raw.turn_revision, 10) || 0
    } : null
    const prev = ctx.state.answerRefresh[root] || null
    ctx.commit('SET_ANSWER_REFRESH', { rootId: root, refresh: next })
    const wasPending = !!prev && isPendingStatus(prev.status)
    const isPending = !!next && isPendingStatus(next.status)
    return wasPending && (!isPending || next.job_id !== prev.job_id)
  }

  // ticket_updates diff. Returns { roots: root ids to refetch, toasts: root -> update }.
  const diffTicketUpdates = (ctx, updates, since) => {
    const { state, commit } = ctx
    const roots = []
    const refreshRoots = []
    const toasts = {}
    let unknownRoot = false
    const baseline = !rt.baselined
    rt.baselined = true
    updates.forEach(u => {
      const root = parseInt(u && u.root_id, 10)
      if (!(root > 0)) return
      if (applyAnswerRefresh(ctx, root, u)) refreshRoots.push(root)
      const version = parseInt(u.notification_version, 10) || 0
      const prev = rt.rootVersions[root]
      rt.rootVersions[root] = Math.max(prev || 0, version)
      const existing = state.ticketsById[root]
      if (baseline) {
        // No historical toasts. Only catch up a loaded thread rendered at an older version.
        const thread = state.threadsByRootId[root]
        if (thread && thread.loaded && (thread.notificationVersion || 0) < version) roots.push(root)
        return
      }
      if (prev !== undefined && version <= prev) return
      if (existing) {
        commit('UPSERT_TICKETS', {
          tickets: [{
            root_id: root,
            notification_version: Math.max(existing.notification_version || 0, version),
            read_version: Math.max(existing.read_version || 0, parseInt(u.read_version, 10) || 0),
            closed_at: u.closed_at || null
          }],
          since
        })
        commit('PLACE_TICKET', root)
      } else {
        unknownRoot = true
      }
      const thread = state.threadsByRootId[root]
      const last = u.last_notification
      const toastable = !!last && (parseInt(last.version, 10) || 0) === version && isToastableNotification(last)
      if (toastable) toasts[root] = { version, last }
      if (toastable || (thread && thread.loaded) || state.selectedRootId === root) roots.push(root)
    })
    return { roots, refreshRoots, toasts, listsStale: !baseline && (unknownRoot || roots.length > 0) }
  }

  const applyUpdates = (ctx, data, requestedIds, { notify, since }) => {
    const { state, commit } = ctx
    applyCounts(ctx, data)
    const returned = (data && Array.isArray(data.questions)) ? data.questions : []
    const hasTicketUpdates = !!data && Array.isArray(data.ticket_updates)
    const pendingBefore = state.pendingIds.slice()
    const byId = {}
    const arrivals = []
    const settled = []
    returned.forEach(q => {
      byId[q.id] = q
      if (pendingBefore.indexOf(q.id) === -1) return
      if (isArrivalStatus(q.status)) arrivals.push(q)
      if (!isPendingStatus(q.status)) settled.push(q)
    })
    commit('UPSERT_QUESTIONS', returned)
    // Keep the list pill current for in-progress transitions (queued -> running).
    // Only genuine questions drive the ticket status (not notes, system notes or re-answers).
    returned.forEach(q => {
      if (!isGenuineQuestion(q)) return
      const root = rootOf(state, q.id)
      const t = state.ticketsById[root]
      const newest = newestQuestionId(state, state.threadsByRootId[root])
      if (t && (newest === null || newest === q.id) && t.latest_status !== q.status) {
        commit('UPSERT_TICKETS', [{ ...t, latest_status: q.status }])
      }
    })
    const nextPending = pendingBefore.filter(id => {
      if (requestedIds.indexOf(id) === -1) return true
      const q = byId[id]
      if (!q) return false // not ours / gone: stop asking for it
      return isPendingStatus(q.status)
    })
    commit('SET_PENDING', nextPending)
    persistPending(state)

    // A settled row changed its ticket: refetch the thread once per ticket.
    const roots = settled.map(q => rootOf(state, q.id))
    let toasts = {}
    let listsStale = false
    if (hasTicketUpdates) {
      // ticket_updates lists every owned root: a known root missing from it was transferred away.
      const listed = data.ticket_updates.map(u => parseInt(u && u.root_id, 10)).filter(n => n > 0)
      goneRoots(state, listed, since).forEach(r => dropRoot(ctx, r))
      const diff = diffTicketUpdates(ctx, data.ticket_updates, since)
      diff.roots.forEach(r => roots.push(r))
      diff.refreshRoots.forEach(r => {
        const thread = state.threadsByRootId[r]
        if ((thread && thread.loaded) || state.selectedRootId === r) roots.push(r)
      })
      toasts = diff.toasts
      listsStale = diff.listsStale
    }
    // Legacy server: one toast per poll for the latest pending -> arrival.
    const latest = (!hasTicketUpdates && arrivals.length) ? arrivals.reduce((a, b) => (b.id > a.id ? b : a)) : null
    const legacyToastRoot = (notify && latest && !state.drawerOpen) ? rootOf(state, latest.id) : null

    dedupe(roots).forEach(root => {
      ctx.dispatch('loadThread', { rootId: root, silent: true }).then(() => {
        if (!notify || !state.enabled || state.drawerOpen) return
        if (toasts[root]) {
          const { version, last } = toasts[root]
          const row = state.questions[parseInt(last.question_id, 10)] || null
          // Always the ordinary system wording (no management titles).
          enqueueToast(ctx, {
            key: toastKey(root, version),
            rootId: root,
            id: row ? row.id : (parseInt(last.question_id, 10) || null),
            kind: last.kind,
            status: row ? row.status : 'answered',
            text: row ? toastText(row.answer || row.answer_long) : ''
          })
          return
        }
        if (root !== legacyToastRoot) return
        const t = state.ticketsById[root]
        const version = t && t.notification_version ? t.notification_version : 'q' + latest.id
        enqueueToast(ctx, {
          key: toastKey(root, version),
          rootId: root,
          id: latest.id,
          status: latest.status,
          text: toastText(latest.answer || latest.answer_long)
        })
      })
    })
    if (listsStale) {
      invalidateLists()
      ctx.dispatch('loadTickets', { tab: 'open' })
      if (state.listMeta.closed.loaded) ctx.dispatch('loadTickets', { tab: 'closed' })
    }
  }

  const bindVisibility = ctx => {
    if (!deps.doc || rt.visibilityHandler) return
    rt.visibilityHandler = () => {
      if (isHidden()) {
        clearTimer()
        return
      }
      if (ctx.state.enabled && !rt.inFlight) {
        clearTimer()
        ctx.dispatch('pollNow')
      }
    }
    deps.doc.addEventListener('visibilitychange', rt.visibilityHandler)
  }

  const lifecycle = async (ctx, rootId, call) => {
    invalidateLists()
    try {
      const t = unwrapTicket(await call(rootId))
      invalidateLists()
      if (t && t.root_id) placeTicket(ctx, t, { local: true })
      return { ok: true }
    } catch (e) {
      invalidateLists()
      const err = deps.mapError(e)
      if (handleFatal(ctx, err)) return { ok: false, message: '' }
      return { ok: false, error: err, message: TICKET_ERROR_MESSAGES.lifecycle }
    }
  }

  return {
    namespaced: true,
    state: initialState,
    getters: {
      enabled: state => state.enabled,
      supervisor: state => state.enabled && state.supervisor === true,
      supervisorWrites: state => state.enabled && state.supervisor === true && state.supervisorWrites === true,
      jiraWrites: state => state.enabled && state.supervisor === true && state.supervisorWrites === true && state.jiraWrites === true,
      unreadCount: state => state.unreadCounts.total || 0,
      closedHasUnread: state => (state.unreadCounts.closed || 0) > 0,
      currentToast: state => state.toastQueue[0] || null,
      workerOffline: state => !!state.worker.availability && state.worker.availability !== 'online',
      isPending: state => id => state.pendingIds.indexOf(id) !== -1,
      rootIdOf: state => id => rootOf(state, id),
      ticketsFor: state => tab => state[listKey(tab)].map(id => state.ticketsById[id]).filter(Boolean),
      hasMore: state => tab => !!state.cursors[tab === 'closed' ? 'closed' : 'open'],
      ticket: state => rootId => state.ticketsById[rootId] || null,
      thread: state => rootId => state.threadsByRootId[rootId] || null,
      // Public conversation only: no management rows; a re-answer job only once
      // it published (answered / needs_input), placed under its original question.
      threadTurns: state => rootId => {
        const thread = state.threadsByRootId[rootId]
        if (!thread) return []
        const rows = thread.ids.map(id => state.questions[id]).filter(q =>
          !!q && !isPrivateRow(q) && (!isReAnswer(q) || PUBLISHED_REANSWER_STATUSES.indexOf(q.status) !== -1))
        return groupTurns(rows)
      },
      // Pending management re-answer of a ticket (drives the neutral hint), or null.
      answerRefresh: state => rootId => {
        const r = state.answerRefresh[rootId]
        return r && isPendingStatus(r.status) ? r : null
      }
    },
    mutations: {
      SET_ENABLED (state, value) { state.enabled = !!value },
      SET_SUPERVISOR (state, value) { state.supervisor = value === true },
      SET_SUPERVISOR_WRITES (state, value) { state.supervisorWrites = value === true },
      SET_JIRA_WRITES (state, value) { state.jiraWrites = value === true },
      SET_ADMIN (state, { id, displayName }) {
        state.adminId = (id === undefined) ? null : id
        if (displayName !== undefined) state.adminDisplayName = displayName || ''
      },
      UPSERT_QUESTIONS (state, list) {
        const next = { ...state.questions }
        ;(list || []).forEach(raw => {
          if (!raw || !raw.id) return
          const q = sanitizeRecruiterRow(raw)
          if (!q) { delete next[raw.id]; return } // management-only: never kept
          const existing = next[q.id] && sanitizeRecruiterRow(next[q.id])
          const tOld = timeOf(existing)
          const tNew = timeOf(q)
          if (existing && tOld !== null && tNew !== null && tNew < tOld) return // stale
          next[q.id] = existing ? { ...existing, ...q } : q
        })
        state.questions = next
      },
      SET_ROOTS (state, { rootId, ids }) {
        const next = { ...state.rootByQid }
        ids.forEach(id => { next[id] = rootId })
        state.rootByQid = next
      },
      // Drop cached management rows / attribution (old payloads, pre-rollout cache).
      PURGE_PRIVATE (state) {
        const next = {}
        Object.keys(state.questions).forEach(id => {
          const q = sanitizeRecruiterRow(state.questions[id])
          if (q) next[id] = q
        })
        state.questions = next
        const threads = {}
        Object.keys(state.threadsByRootId).forEach(root => {
          const t = state.threadsByRootId[root]
          threads[root] = { ...t, ids: (t.ids || []).filter(id => !!next[id]) }
        })
        state.threadsByRootId = threads
      },
      DROP_ROOT (state, rootId) {
        const without = (obj, key) => { const next = { ...obj }; delete next[key]; return next }
        state.ticketsById = without(state.ticketsById, rootId)
        state.threadsByRootId = without(state.threadsByRootId, rootId)
        state.answerRefresh = without(state.answerRefresh, rootId)
        state.openIds = state.openIds.filter(id => id !== rootId)
        state.closedIds = state.closedIds.filter(id => id !== rootId)
        const roots = {}
        Object.keys(state.rootByQid).forEach(qid => {
          if (state.rootByQid[qid] !== rootId) roots[qid] = state.rootByQid[qid]
        })
        state.rootByQid = roots
        if (state.selectedRootId === rootId) {
          state.selectedRootId = null
          state.view = 'list'
        }
      },
      SET_TRANSFER_NOTICE (state, text) { state.transferNotice = text || '' },
      SET_ANSWER_REFRESH (state, { rootId, refresh }) {
        state.answerRefresh = { ...state.answerRefresh, [rootId]: refresh || null }
      },
      SET_PENDING (state, ids) { state.pendingIds = dedupe(ids || []) },
      // Accepts a list, or { tickets, since } where `since` is the lifecycle
      // tick at which the producing request started. An older summary never
      // overwrites a newer local lifecycle / activity state (cf. UPSERT_QUESTIONS).
      UPSERT_TICKETS (state, payload) {
        const list = Array.isArray(payload) ? payload : ((payload && payload.tickets) || [])
        const since = Array.isArray(payload) ? undefined : (payload && payload.since)
        const next = { ...state.ticketsById }
        list.forEach(t => {
          if (!t || !t.root_id) return
          const existing = next[t.root_id]
          let incoming = t
          // A local lifecycle change (or the server's answer to it) always applies.
          if (existing && !t._lifecycle_tick) {
            const tOld = existing.last_activity_at ? Date.parse(existing.last_activity_at) : NaN
            const tNew = t.last_activity_at ? Date.parse(t.last_activity_at) : NaN
            const olderActivity = !isNaN(tOld) && !isNaN(tNew) && tNew < tOld
            const beforeLocalChange = typeof since === 'number' && since < (existing._lifecycle_tick || 0)
            if (olderActivity || beforeLocalChange) {
              incoming = { ...t }
              LIFECYCLE_FIELDS.forEach(f => { delete incoming[f] })
            }
          }
          const merged = existing ? { ...existing, ...incoming } : { ...incoming }
          // Versions only grow server-side: an out-of-order summary never regresses them.
          if (existing) {
            ;['notification_version', 'read_version'].forEach(f => {
              if (typeof existing[f] === 'number' && typeof merged[f] === 'number' && merged[f] < existing[f]) merged[f] = existing[f]
            })
          }
          if (!Array.isArray(merged.jira_task_ids)) merged.jira_task_ids = []
          merged.unread = isTicketUnread(merged)
          next[t.root_id] = merged
        })
        state.ticketsById = next
      },
      // Replace (page 1) or append (next page) a tab's ids, keeping each root in one list.
      SET_LIST (state, { tab, ids, cursor, append }) {
        const key = listKey(tab)
        const other = listKey(tab === 'closed' ? 'open' : 'closed')
        const nextIds = dedupe((append ? state[key] : []).concat(ids))
        state[key] = nextIds
        state[other] = state[other].filter(id => nextIds.indexOf(id) === -1)
        state.cursors = { ...state.cursors, [tab]: cursor || null }
      },
      PLACE_TICKET (state, rootId) {
        const t = state.ticketsById[rootId]
        if (!t) return
        const tab = tabOfTicket(t)
        const key = listKey(tab)
        const other = listKey(tab === 'closed' ? 'open' : 'closed')
        state[other] = state[other].filter(id => id !== rootId)
        state[key] = sortTicketIds(dedupe(state[key].concat([rootId])), state.ticketsById)
      },
      SET_LIST_META (state, { tab, ...meta }) {
        state.listMeta = { ...state.listMeta, [tab]: { ...state.listMeta[tab], ...meta } }
      },
      SET_THREAD (state, { rootId, ...patch }) {
        const prev = state.threadsByRootId[rootId] || { ids: [], notificationVersion: 0, loading: false, error: '', loaded: false }
        state.threadsByRootId = { ...state.threadsByRootId, [rootId]: { ...prev, ...patch } }
      },
      SET_UNREAD_COUNTS (state, counts) {
        const n = v => (parseInt(v, 10) > 0 ? parseInt(v, 10) : 0)
        state.unreadCounts = { open: n(counts.open), closed: n(counts.closed), total: n(counts.total) }
      },
      SET_DRAWER (state, open) { state.drawerOpen = !!open },
      SET_VIEW (state, view) { state.view = ['list', 'thread', 'new'].indexOf(view) !== -1 ? view : 'list' },
      SET_TAB (state, tab) { state.activeTab = tab === 'closed' ? 'closed' : 'open' },
      SELECT_ROOT (state, rootId) { state.selectedRootId = rootId || null },
      SET_LIST_SCROLL (state, { tab, top }) { state.listScroll = { ...state.listScroll, [tab]: top || 0 } },
      SET_DRAFT (state, { key, text }) { state.drafts = { ...state.drafts, [key]: text || '' } },
      SET_NEW_CONTEXT (state, ctx) { state.newContext = ctx || {} },
      SET_WORKER (state, worker) {
        state.worker = {
          availability: worker.availability || null,
          last_seen_at: worker.last_seen_at || null
        }
      },
      ENQUEUE_TOAST (state, toast) {
        if (state.toastQueue.some(t => t.key === toast.key)) return
        state.toastQueue = state.toastQueue.concat([toast])
      },
      SHIFT_TOAST (state) { state.toastQueue = state.toastQueue.slice(1) },
      CLEAR_TOASTS (state) { state.toastQueue = [] },
      SET_FEEDBACK (state, { id, helpful }) {
        state.feedback = { ...state.feedback, [id]: { helpful: !!helpful } }
      },
      RESET (state) {
        const fresh = initialState()
        Object.keys(fresh).forEach(key => { state[key] = fresh[key] })
      }
    },
    actions: {
      // GET /config once per login; hides the feature on 404.
      init (ctx) {
        if (rt.initPromise) return rt.initPromise
        const generation = rt.generation
        rt.initPromise = (async () => {
          let config
          try {
            config = await deps.api.getConfig()
          } catch (e) {
            if (generation !== rt.generation) return false
            const err = deps.mapError(e)
            if (err.status === 404) err.kind = 'feature_off' // any 404 from /config hides
            if (!handleFatal(ctx, err)) rt.initPromise = null // transient: allow a later retry
            return false
          }
          if (generation !== rt.generation) return false
          if (!config || config.enabled === false) {
            stopAndClear(ctx)
            return false
          }
          // Prefer the server's admin_id (stable across owner-select changes);
          // fall back to the owner-select localStorage entry.
          const serverAdminId = parseInt(config.admin_id, 10)
          const adminId = serverAdminId > 0 ? serverAdminId : deps.getAdminId()
          ctx.commit('SET_ADMIN', { id: adminId === undefined ? null : adminId, displayName: config.admin_display_name })
          ctx.commit('SET_SUPERVISOR', config.supervisor)
          ctx.commit('SET_SUPERVISOR_WRITES', config.supervisor_writes)
          ctx.commit('SET_JIRA_WRITES', config.jira_writes)
          ctx.commit('SET_ENABLED', true)
          ctx.commit('SET_PENDING', loadPending(ctx.state.adminId))
          bindVisibility(ctx)
          // Page 1 of open tickets: gives the badge (unread_counts). Never toasts.
          await ctx.dispatch('loadTickets', { tab: 'open' })
          // First /updates right away: it is the ticket_updates baseline.
          schedule(ctx, 0)
          return true
        })()
        return rt.initPromise
      },

      // GET /tickets for a tab; `more` appends the next page. Never toasts.
      async loadTickets (ctx, { tab = 'open', more = false, retried = false } = {}) {
        if (!ctx.state.enabled) return
        const generation = rt.generation
        const cursor = more ? ctx.state.cursors[tab] : null
        if (more && !cursor) return
        const seq = ++rt.listSeq[tab]
        rt.listReq[tab] = seq
        const since = rt.lifecycleTick
        ctx.commit('SET_LIST_META', { tab, loading: true, error: '' })
        // Stale response: a newer loadTickets owns the meta; if a local
        // submit/close/reopen made it stale instead, fetch once more.
        const discard = () => {
          if (generation !== rt.generation || rt.listReq[tab] !== seq) return
          if (!retried) return ctx.dispatch('loadTickets', { tab, more, retried: true })
          ctx.commit('SET_LIST_META', { tab, loading: false })
        }
        let data
        try {
          data = await deps.api.listTickets({ state: tab, cursor, limit: qaApi.TICKETS_PAGE_SIZE })
        } catch (e) {
          if (generation !== rt.generation || seq !== rt.listSeq[tab]) return discard()
          if (handleFatal(ctx, deps.mapError(e))) return
          ctx.commit('SET_LIST_META', { tab, loading: false, error: TICKET_ERROR_MESSAGES.list })
          return
        }
        if (generation !== rt.generation || seq !== rt.listSeq[tab]) return discard()
        applyCounts(ctx, data)
        const tickets = (data && Array.isArray(data.tickets)) ? data.tickets.filter(t => t && t.root_id) : []
        // A complete tab list (page 1, no next page) that lacks a root we show in
        // that tab: moved to the other tab or transferred away. Ask the thread
        // endpoint once; its 404 drops the root quietly (see loadThread).
        const complete = !more && !(data && data.next_cursor)
        const missing = complete
          ? goneRoots(ctx.state, tickets.map(t => t.root_id), since)
            .filter(id => ctx.state[listKey(tab)].indexOf(id) !== -1)
          : []
        ctx.commit('UPSERT_TICKETS', { tickets, since })
        ctx.commit('SET_LIST', { tab, ids: tickets.map(t => t.root_id), cursor: data && data.next_cursor, append: more })
        ctx.commit('SET_LIST_META', { tab, loading: false, loaded: true, error: '' })
        missing.forEach(id => { ctx.dispatch('loadThread', { rootId: id, silent: true }) })
      },

      // GET /:root_id/thread. Newest request per root wins.
      async loadThread (ctx, { rootId, silent = false }) {
        if (!ctx.state.enabled || !rootId) return null
        const generation = rt.generation
        const seq = (rt.threadSeq[rootId] || 0) + 1
        rt.threadSeq[rootId] = seq
        if (!silent) ctx.commit('SET_THREAD', { rootId, loading: true, error: '' })
        const since = rt.lifecycleTick
        let data
        try {
          data = await deps.api.getThread(rootId)
        } catch (e) {
          if (generation !== rt.generation || seq !== rt.threadSeq[rootId]) return null
          const err = deps.mapError(e)
          if (handleFatal(ctx, err)) return null
          // 404 for a root we hold = ownership moved: drop it quietly (not an error).
          const known = ctx.state.ticketsById[rootId] || (ctx.state.threadsByRootId[rootId] && ctx.state.threadsByRootId[rootId].loaded)
          if (err.kind === 'not_found' && known) {
            dropRoot(ctx, rootId)
            return null
          }
          if (!silent) {
            ctx.commit('SET_THREAD', {
              rootId,
              loading: false,
              error: err.kind === 'not_found' ? TICKET_ERROR_MESSAGES.not_found : TICKET_ERROR_MESSAGES.thread
            })
          }
          return null
        }
        if (generation !== rt.generation || seq !== rt.threadSeq[rootId]) return null
        const rows = (data && Array.isArray(data.questions)) ? data.questions.filter(q => q && q.id && !isPrivateRow(q)) : []
        const ids = rows.map(q => q.id).sort((a, b) => a - b)
        ctx.commit('PURGE_PRIVATE')
        ctx.commit('UPSERT_QUESTIONS', rows)
        ctx.commit('SET_ROOTS', { rootId, ids })
        const ticket = data && data.ticket ? { root_id: rootId, ...data.ticket } : null
        if (ticket) placeTicket(ctx, ticket, { since })
        if (data && data.ticket) applyAnswerRefresh(ctx, rootId, data.ticket)
        ctx.commit('SET_THREAD', {
          rootId,
          ids,
          notificationVersion: ticket ? (ticket.notification_version || 0) : 0,
          loading: false,
          loaded: true,
          truncated: !!(data && data.truncated),
          error: ''
        })
        // Rows still in progress join the poll; settled ones leave it.
        const settledIds = rows.filter(q => !isPendingStatus(q.status)).map(q => q.id)
        const trimmed = ctx.state.pendingIds.filter(id => settledIds.indexOf(id) === -1)
        if (trimmed.length !== ctx.state.pendingIds.length) {
          ctx.commit('SET_PENDING', trimmed)
          persistPending(ctx.state)
        }
        addPending(ctx, rows.filter(q => isPendingStatus(q.status)).map(q => q.id))
        if (!rt.inFlight && !rt.timer) schedule(ctx)
        return data
      },

      async pollNow (ctx) {
        const { state } = ctx
        if (rt.inFlight || !state.enabled || isHidden()) return
        clearTimer()
        rt.inFlight = true
        const generation = rt.generation
        const since = rt.lifecycleTick
        const ids = state.pendingIds.slice(0, MAX_IDS_PER_POLL)
        let data
        try {
          data = await deps.api.getUpdates(ids)
        } catch (e) {
          if (generation !== rt.generation) return // obsolete cycle
          rt.inFlight = false
          const err = deps.mapError(e)
          if (err.status === 404) err.kind = 'feature_off' // 404 from /updates hides
          if (handleFatal(ctx, err)) return
          rt.errorCount++
          schedule(ctx)
          return
        }
        if (generation !== rt.generation) return // stale response
        rt.inFlight = false
        rt.errorCount = 0
        applyUpdates(ctx, data, ids, { notify: true, since })
        schedule(ctx)
      },

      // New request (parentId null) or follow-up (parentId = any row of the
      // ticket; the server flattens to the root and reopens it).
      async submit (ctx, { question, parentId, context, requestUuid, screenshot }) {
        if (!ctx.state.enabled) return { ok: false, message: qaApi.ERROR_MESSAGES.network }
        const uuid = requestUuid || deps.uuid()
        const parentRoot = parentId ? rootOf(ctx.state, parentId) : null
        invalidateLists()
        let q
        try {
          q = unwrapQuestion(await deps.api.createQuestion({
            request_uuid: uuid,
            question,
            parent_id: parentRoot,
            context: context || {},
            screenshot: screenshot || null
          }))
        } catch (e) {
          invalidateLists()
          const err = deps.mapError(e)
          handleFatal(ctx, err)
          return { ok: false, error: err, message: err.message, requestUuid: uuid }
        }
        invalidateLists()
        if (!q || !q.id) return { ok: true, question: q, rootId: parentRoot }
        const rootId = parentRoot || q.id
        const activity = q.created_at || new Date(deps.now()).toISOString()
        ctx.commit('UPSERT_QUESTIONS', [q])
        ctx.commit('SET_ROOTS', { rootId, ids: [q.id] })
        const thread = ctx.state.threadsByRootId[rootId]
        const ids = dedupe(((thread && thread.ids) || []).concat([q.id])).sort((a, b) => a - b)
        ctx.commit('SET_THREAD', { rootId, ids, loaded: true })
        const existing = ctx.state.ticketsById[rootId]
        placeTicket(ctx, existing
          ? { root_id: rootId, latest_status: q.status, last_activity_at: activity, closed_at: null }
          : {
            root_id: rootId,
            title: ticketTitle(q.question || question),
            latest_status: q.status,
            last_activity_at: activity,
            closed_at: null,
            notification_version: 0,
            read_version: 0,
            jira_task_ids: [],
            context: q.context || context || {}
          }, { local: true })
        if (isPendingStatus(q.status)) addPending(ctx, [q.id])
        if (!rt.inFlight) schedule(ctx)
        return { ok: true, question: q, rootId }
      },

      // "נסי שוב" on a failed turn: a NEW row with a new uuid, same ticket.
      retry (ctx, { id }) {
        const q = ctx.state.questions[id]
        if (!q) return Promise.resolve({ ok: false, message: qaApi.ERROR_MESSAGES.network })
        // Management cancelled this turn: no generic retry (SUPERVISOR_SPEC §3).
        if (isCancelledBySupervisor(q) || isPrivateRow(q) || isReAnswer(q)) return Promise.resolve({ ok: false, message: '' })
        const c = q.context || {}
        const context = { route_name: c.route_name || '' }
        ;['applicant_id', 'position_id', 'offer_id'].forEach(k => { if (c[k]) context[k] = c[k] })
        const root = rootOf(ctx.state, id)
        return ctx.dispatch('submit', { question: q.question, parentId: root !== id ? root : (q.parent_id || id), context })
      },

      closeTicket (ctx, { rootId }) { return lifecycle(ctx, rootId, deps.api.closeTicket) },
      reopenTicket (ctx, { rootId }) { return lifecycle(ctx, rootId, deps.api.reopenTicket) },

      // Read-ack: only the version actually rendered, only while that thread
      // is the visible view in a focused, visible document.
      async ackRead (ctx, { rootId }) {
        const { state } = ctx
        if (!state.enabled || !state.drawerOpen || state.view !== 'thread' || state.selectedRootId !== rootId) return false
        if (isHidden() || !deps.hasFocus()) return false
        const thread = state.threadsByRootId[rootId]
        const ticket = state.ticketsById[rootId]
        if (!thread || !thread.loaded || !ticket) return false
        // A truncated thread was not fully rendered: never ack it as read.
        if (thread.truncated) return false
        const version = thread.notificationVersion || 0
        if (version <= (ticket.read_version || 0)) return false
        if (rt.ackInFlight[rootId] >= version) return false
        rt.ackInFlight[rootId] = version
        const generation = rt.generation
        let data
        try {
          data = await deps.api.markTicketRead(rootId, version)
        } catch (e) {
          if (generation === rt.generation) {
            delete rt.ackInFlight[rootId]
            handleFatal(ctx, deps.mapError(e))
          }
          return false
        }
        if (generation !== rt.generation) return false
        delete rt.ackInFlight[rootId]
        const current = state.ticketsById[rootId]
        if (!current) return true
        const readVersion = Math.max(current.read_version || 0, parseInt(data && data.read_version, 10) || version)
        const wasUnread = isTicketUnread(current)
        ctx.commit('UPSERT_TICKETS', [{ root_id: rootId, read_version: readVersion }])
        if (wasUnread && !isTicketUnread(state.ticketsById[rootId])) {
          // Local estimate until the next server count arrives.
          const c = state.unreadCounts
          const tab = tabOfTicket(current)
          ctx.commit('SET_UNREAD_COUNTS', { ...c, [tab]: Math.max(0, c[tab] - 1), total: Math.max(0, c.total - 1) })
        }
        return true
      },

      async sendFeedback (ctx, { id, helpful, reason }) {
        try {
          await deps.api.sendFeedback(id, { helpful, reason })
        } catch (e) {
          const err = deps.mapError(e)
          handleFatal(ctx, err)
          return { ok: false, message: err.message }
        }
        ctx.commit('SET_FEEDBACK', { id, helpful })
        return { ok: true }
      },

      async fetchContextNames (ctx, context) {
        try {
          return { ok: true, names: await deps.api.getContext(context || {}) }
        } catch (e) {
          const err = deps.mapError(e)
          handleFatal(ctx, err)
          return { ok: false, error: err, message: err.message }
        }
      },

      // A fresh opening always lands on the list, tab "פתוחות" — unless a
      // toast / deep link names a ticket.
      openDrawer (ctx, { rootId } = {}) {
        if (!ctx.state.enabled) return
        const wasOpen = ctx.state.drawerOpen
        ctx.commit('SET_DRAWER', true)
        ctx.commit('CLEAR_TOASTS')
        if (rootId) {
          ctx.dispatch('openTicket', { rootId })
        } else if (!wasOpen) {
          ctx.commit('SET_VIEW', 'list')
          ctx.commit('SET_TAB', 'open')
          ctx.commit('SET_LIST_SCROLL', { tab: 'open', top: 0 })
          ctx.dispatch('loadTickets', { tab: 'open' })
        }
        if (!rt.inFlight) schedule(ctx)
      },

      closeDrawer (ctx) {
        ctx.commit('SET_TRANSFER_NOTICE', '')
        ctx.commit('SET_DRAWER', false)
        ctx.commit('SET_VIEW', 'list')
        ctx.commit('SELECT_ROOT', null)
        if (!rt.inFlight) schedule(ctx)
      },

      openTicket (ctx, { rootId }) {
        ctx.commit('SET_TRANSFER_NOTICE', '')
        ctx.commit('SELECT_ROOT', rootId)
        ctx.commit('SET_VIEW', 'thread')
        return ctx.dispatch('loadThread', { rootId })
      },

      backToList (ctx) {
        ctx.commit('SET_VIEW', 'list')
        const meta = ctx.state.listMeta[ctx.state.activeTab]
        if (!meta.loaded) ctx.dispatch('loadTickets', { tab: ctx.state.activeTab })
      },

      startNew (ctx) { ctx.commit('SET_VIEW', 'new') },

      setTab (ctx, { tab }) {
        ctx.commit('SET_TAB', tab)
        ctx.commit('SET_VIEW', 'list')
        return ctx.dispatch('loadTickets', { tab: ctx.state.activeTab })
      },

      dismissToast (ctx) {
        ctx.commit('SHIFT_TOAST')
      },

      openFromToast (ctx, { rootId }) {
        ctx.commit('SHIFT_TOAST')
        return ctx.dispatch('openDrawer', { rootId })
      },

      // Deep link ?recruiter_question=<id>: resolve the root via GET /:id
      // (walking parent_id for legacy nested rows), then open its thread.
      async openQuestion (ctx, { id }) {
        const qid = parseInt(id, 10)
        if (!(qid > 0)) return
        if (rt.initPromise) await rt.initPromise
        if (!ctx.state.enabled) return
        let rootId = ctx.state.rootByQid[qid] || null
        let current = qid
        for (let hops = 0; !rootId && hops < 5; hops++) {
          let q
          try {
            q = unwrapQuestion(await deps.api.getQuestion(current))
          } catch (e) {
            if (handleFatal(ctx, deps.mapError(e))) return
            break
          }
          if (!ctx.state.enabled || !q || !q.id) break
          ctx.commit('UPSERT_QUESTIONS', [q])
          if (!q.parent_id) rootId = q.id
          else current = q.parent_id
        }
        // Unresolvable: open the requested id; the thread view shows "not found".
        ctx.dispatch('openDrawer', { rootId: rootId || qid })
      },

      // Logout / account switch.
      reset (ctx) {
        stopAndClear(ctx)
      }
    }
  }
}

export default createRecruiterQaModule()
